import { useCallback, useEffect, useRef } from 'react';

export type DebouncedCallback<T extends (...args: any[]) => void> = T & {
  cancel: () => void;
};

export function useDebouncedCallback<T extends (...args: any[]) => void>(
  callback: T,
  delay: number
): DebouncedCallback<T> {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  const cancel = useCallback(() => clearTimeout(timeoutRef.current), []);

  const debounced = useCallback(
    (...args: Parameters<T>) => {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(
        () => callbackRef.current(...args),
        delay
      );
    },
    [delay]
  ) as DebouncedCallback<T>;

  debounced.cancel = cancel;
  return debounced;
}
